import { useState } from "react";
import { AlertCircle, Loader, Mail, CheckCircle } from "lucide-react";
import { forgotPassword } from "../api/services";

export default function ForgotPasswordModal({ open, onClose, onBackToLogin }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError("Please enter your email address");
      return;
    }
    
    if (!email.includes("@") || !email.includes(".")) {
      setError("Please enter a valid email address");
      return;
    }
    
    setIsLoading(true);
    setError("");
    
    try {
      const response = await forgotPassword({ email });
      console.log("Forgot password response:", response);
      
      // Check if response indicates success
      if (response.success || response.message?.includes("sent") || response.message?.includes("reset")) {
        setSuccess(true);
      } else {
        setError(response.message || "Failed to send reset link. Please try again.");
      }
    } catch (err) {
      console.error("Forgot password error:", err);
      setError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setEmail("");
    setError("");
    setSuccess(false);
    onBackToLogin();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md overflow-hidden bg-white shadow-2xl rounded-2xl">
        {!success ? (
          // Forgot Password Form
          <>
            <div className="flex flex-col items-center px-6 pb-4 text-center pt-7">
              <div className="flex items-center justify-center mb-4 w-14 h-14 rounded-2xl bg-[#009DB9]/10">
                <Mail size={28} className="text-[#009DB9]" strokeWidth={1.5} />
              </div>
              <h2 className="text-[18px] font-bold text-neutral-900">Forgot Password?</h2>
              <p className="text-[13px] text-neutral-500 mt-2">
                Enter your email address and we'll send you
                <br />a link to reset your password.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="px-6 pb-6">
              <div className="mb-4">
                <label className="block mb-1 text-xs font-semibold tracking-widest text-gray-500 uppercase">
                  Email Address
                </label>
                <div className="flex items-center gap-3 border-b-2 border-gray-200 pb-2 focus-within:border-[#009DB9] transition-colors duration-200">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="flex-shrink-0"
                    style={{ color: email ? "#009DB9" : "#9ca3af" }}
                  >
                    <path
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError("");
                    }}
                    placeholder="you@example.com"
                    autoComplete="email"
                    autoFocus
                    className="flex-1 bg-transparent outline-none text-gray-800 text-sm placeholder-gray-300 pb-0.5"
                  />
                </div>
                {error && (
                  <div className="flex items-center gap-1.5 mt-2 text-red-500 text-xs">
                    <AlertCircle size={12} />
                    <span>{error}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2.5 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 h-10 rounded-xl text-[13px] font-semibold border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 h-10 rounded-xl text-[13px] font-semibold text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ background: "#009DB9" }}
                  onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.background = "#007a90"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#009DB9"; }}
                >
                  {isLoading ? <Loader size={14} className="animate-spin" /> : "Send Reset Link"}
                </button>
              </div>
              
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="text-[11px] font-medium transition-colors hover:underline"
                  style={{ color: "#009DB9" }}
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          </>
        ) : (
          // Success State
          <>
            <div className="flex flex-col items-center px-6 pb-4 text-center pt-7">
              <div className="flex items-center justify-center mb-4 w-14 h-14 rounded-2xl bg-green-50">
                <CheckCircle size={28} className="text-green-500" strokeWidth={1.5} />
              </div>
              <h2 className="text-[18px] font-bold text-neutral-900">Check Your Email</h2>
              <p className="text-[13px] text-neutral-500 mt-2">
                We've sent a password reset link to
              </p>
              <p className="text-[14px] font-semibold text-neutral-800 mt-1">{email}</p>
              <p className="text-[11px] text-neutral-400 mt-3">
                Didn't receive the email? Check your spam folder
                <br />or try again.
              </p>
            </div>

            <div className="px-6 pb-6">
              <div className="flex gap-2.5">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="flex-1 h-10 rounded-xl text-[13px] font-semibold text-white transition-colors"
                  style={{ background: "#009DB9" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#007a90"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#009DB9"; }}
                >
                  Back to Sign In
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}